import React, { useState, useEffect } from 'react';
import { createCheckoutSession } from '../utils/stripe';
import { loadSettings } from '../utils/firebaseSettings';

const PricingModal = ({ isOpen, onClose, userEmail, requireSubscription = false }) => {
  const [loading, setLoading] = useState(false);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [checkingTrial, setCheckingTrial] = useState(true);

  // Check if user has used trial before using Firebase settings
  useEffect(() => {
    const checkTrialStatus = async () => {
      if (!userEmail) return;

      setCheckingTrial(true);
      try {
        // Load user settings from Firebase to check hasUsedTrial flag
        const settings = await loadSettings(userEmail);
        setHasUsedTrial(settings.hasUsedTrial || false);
        console.log('Trial status from Firebase:', settings.hasUsedTrial ? 'Used' : 'Available');
      } catch (error) {
        console.error('Error checking trial status:', error);
        // Default to false (allow trial) if there's an error
        setHasUsedTrial(false);
      }
      setCheckingTrial(false);
    };

    if (isOpen) {
      checkTrialStatus();
    }
  }, [isOpen, userEmail]);

  const handleSubscribe = async (priceId, planName) => {
    setLoading(true);
    try {
      await createCheckoutSession(priceId, userEmail);
    } catch (error) {
      alert(`Failed to start checkout: ${error.message}`);
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Choose Your Plan</h2>
            <p className="text-sm text-gray-600 mt-1">
              {checkingTrial ? 'Loading...' : hasUsedTrial ? 'Select your subscription' : '✨ Start with 3 days free trial'}
            </p>
          </div>
          {!requireSubscription && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="p-8 grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {/* Basic Plan */}
          <div className="bg-white rounded-2xl border border-gray-200 p-10 hover:shadow-lg transition-shadow flex flex-col">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-1">Basic Plan</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold text-gray-900">$2.99</span>
                <span className="text-gray-500">/month</span>
              </div>
              {!hasUsedTrial && <p className="text-sm text-blue-500 font-medium mt-3">Includes 3-day free trial</p>}
            </div>

            <ul className="space-y-4 mb-10 flex-grow">
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">Full Kanban board access</span>
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">Gmail integration</span>
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">Email management</span>
              </li>
            </ul>

            <div className="mt-auto">
              <button
                onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_BASIC_PRICE_ID, 'Basic')}
                disabled={loading || checkingTrial}
                className="w-full py-3.5 px-6 border-2 border-gray-200 text-gray-900 font-semibold rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all hover:scale-105 transform disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <span>{loading ? 'Loading...' : checkingTrial ? 'Loading...' : hasUsedTrial ? 'Get Started' : 'Start 3 day free trial'}</span>
                {!loading && !checkingTrial && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                )}
              </button>
              {!hasUsedTrial && <p className="text-center text-sm text-gray-500 mt-3">$0.00 due today, cancel anytime</p>}
            </div>
          </div>

          {/* Pro Plan */}
          <div className="bg-white rounded-2xl border-2 border-blue-500 p-10 relative hover:shadow-xl transition-shadow flex flex-col">
            <div className="absolute -top-3 left-8">
              <span className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">Popular</span>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-1">Pro Plan</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold text-gray-900">$9.99</span>
                <span className="text-gray-500">/month</span>
              </div>
              {!hasUsedTrial && <p className="text-sm text-blue-500 font-medium mt-3">Includes 3-day free trial</p>}
            </div>

            <ul className="space-y-4 mb-10 flex-grow">
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">Everything in Basic</span>
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-900 font-semibold">AI email generation</span>
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">Custom knowledge base</span>
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">5 response personalities</span>
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">Priority support</span>
              </li>
            </ul>

            <div className="mt-auto">
              <button
                onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_PRO_PRICE_ID, 'Pro')}
                disabled={loading || checkingTrial}
                className="w-full py-3.5 px-6 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl hover:scale-105 transform disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <span>{loading ? 'Loading...' : checkingTrial ? 'Loading...' : hasUsedTrial ? 'Get Started' : 'Start 3 day free trial'}</span>
                {!loading && !checkingTrial && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                )}
              </button>
              {!hasUsedTrial && <p className="text-center text-sm text-gray-500 mt-3">$0.00 due today, cancel anytime</p>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 text-center">
          <p className="text-sm text-gray-600">
            💳 Secure payment powered by Stripe {!hasUsedTrial && '• 3 days free trial'} • Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
